/**
 * Triangle Sweeper の定数。**定数の唯一のソース**（マジックナンバーを散らさない）。
 *
 * 出典は原作 iPad 版 `Supporting Files/GlobalDefines.h` / `Classes/ShapeModel.h`。
 * ただし**アンロック閾値とスコアは Web 版で決め直した**（原作は下記のとおり詰んでいた／
 * スコアの概念が無かった）。仕様は [docs/PORTING-triangle-sweeper.md](../../../../docs/PORTING-triangle-sweeper.md)。
 *
 * 広告・アプリ内課金（`fullVersion`）・GameCenter の ID は移植しないので持たない。
 */
import { OS_TOP_STRIP_H } from '@/platform/portal/homeButton';

// ----- 戻る矢印（左上・ポータルの DOM ボタンの右へ逃がす） -----

/**
 * 戻る矢印の大きさ（`arrowButton` の `size`）。
 *
 * ⚠️ **この画面の `arrowButton` は押し代が「中心 ± `size`/2」**（`rectangle(x, y, size, size)` を
 *   origin 0.5 で置く実装）。箱プッシュ・トランプ・2048 の同名関数は
 *   `setRectHitArea(g, {x: x - size, w: size * 2})` ＝ **中心 ± `size`** で、**2倍違う**。
 *   同じ名前で判定の広さが違うので、位置を決めるときに他ゲームの式を持ち込まないこと
 *   （2026-08-17 に持ち込んで、重なっていないものを「6px 潜っている」と誤判定した）。
 */
export const HEADER_BACK_SIZE = 38;

// 戻る矢印の位置は DESIGN_WIDTH を使うので、解像度の定義より後ろに置いてある。

// ----- 盤面デザイン解像度 -----
// 原作は iPad ベース。二角取りと同じ 1024×768 を基準にし、Scale.FIT で各デバイスへ合わせる。
export const DESIGN_WIDTH = 1024;
export const DESIGN_HEIGHT = 768;

/** 見出しバーの高さ（各画面）。`GameScene` だけ独自の厚いバーを持つ。 */
export const HEADER_H = 58;
/** プレイ画面のトップバーの高さ。 */
export const TOP_BAR_H = 72;
/** 戻る矢印と画面の端・バーとのすき間。 */
export const HEADER_BACK_GAP = 8;

/**
 * 見出し・情報を置き始める x（画面の端から）。
 *
 * 🔴 **以前はホームボタンの右**（`HOME_BUTTON_RIGHT + 12` ＝ 144）だった。
 *   2026-08-18 に「← ゲーム一覧へ」を**タイトル画面だけ**に出すことにしたので、
 *   この画面群にホームボタンは来ない＝予約が丸ごと遊んでいた。⇒ 端まで詰める。
 */
export const HEADER_EDGE_X = 24;

/**
 * 戻る矢印の中心（**右上・バーの下**）。
 *
 * 🔴 **画面最上部の帯には置けない**（2026-08-18 に本番 iPad で報告）。
 *   iPadOS のウインドウ操作がその帯を取るので、**ダブルタップがサイズ変更モードに化ける**。
 *   以前は形状選択が y=29・プレイ画面が y=36 ＝ どちらも帯のど真ん中にいた。
 *   ⇒ 押し代の上端をバーの下へ逃がす。`OS_TOP_STRIP_H` は薄いバー用の下限。
 *
 * 🔴 **左上はもう使わない**。DOM のホームボタンと OS の帯が二重に効く一等地。
 *   右上へ移して**5本とも右上**に揃えた（2026-08-18 ユーザー判断）。
 *
 * ⚠️ **押し代は中心 ± `size`/2**（この実装だけ他3本の半分）。位置を出すときに
 *   他ゲームの式を持ち込まないこと（2026-08-17 に持ち込んで誤判定した）。
 * ⚠️ プレイ画面は右上に一時停止・リセットが居るので、**その下**になる（y が別）。
 *
 * 見張りは
 * [games/__tests__/backButtonHitArea.test.ts](../../__tests__/backButtonHitArea.test.ts)。
 */
/**
 * 🔴 **左上へ戻した**（2026-08-18・開発 iPad での検証を受けたユーザー指示）。
 *   同日の午前に右上へ逃がしたが、実機で確かめて左上のほうが押しやすいという判断。
 *   ⚠️ **Y は動かさない**＝ `HEADER_BACK_Y`（バーの下）のまま。左上が危ないのは
 *     「帯の中にあるとき」であって、帯より下なら左でも取られない。
 *   ⚠️ これを使うのは `sceneHeader` ＝ **形状選択だけ**（他の画面は `onBack` を渡していない）。
 *     プレイ画面は自前で置いており、**右のまま**（`GAME_BACK_X`・指示に無いので動かさない）。
 *
 * 🔴 **「他のゲームと揃える」で右へ動かさないこと**（2026-09-07・ユーザー判断で現状維持と確定）。
 *   11本のうち **9本が右上**・**ここだけ左上**・二角取りは別形式、という散らばりは把握したうえで
 *   **揃えないと決めた**。⚠️ 左右は**見た目の問題であって安全性の問題ではない**
 *   ——危ないのは帯（`OS_TOP_STRIP_H`）だけで、それは上の `HEADER_BACK_Y` が担保している。
 *   ⇒ **左右をどこへ置いても安全性は変わらない**ので、実機で押しやすいと判断した方を残す。
 *   蒸し返すのは**実機で押し間違えたとき**だけ。経緯は HANDOFF「次にやること」。
 */
export const HEADER_BACK_X = HEADER_BACK_GAP + HEADER_BACK_SIZE / 2;

/** プレイ画面の戻る（右上・トップバーの下）。形状選択とは別に持つ。 */
export const GAME_BACK_X = DESIGN_WIDTH - HEADER_BACK_GAP - HEADER_BACK_SIZE / 2;
export const HEADER_BACK_Y =
  Math.max(HEADER_H, OS_TOP_STRIP_H) + HEADER_BACK_GAP + HEADER_BACK_SIZE / 2;
export const GAME_BACK_Y =
  Math.max(TOP_BAR_H, OS_TOP_STRIP_H) + HEADER_BACK_GAP + HEADER_BACK_SIZE / 2;

// ----- 三角形パネル（原作 kTriSideLength / kTriangleBorder / kShapeSideLength） -----
/** 小三角形の一辺（px）。 */
export const TRI_SIDE = 64;
/** パネルの枠線の太さ（px）。 */
export const TRI_BORDER = 2;
/** 大三角形の一辺 ＝ 小三角形4つ分。 */
export const SHAPE_SIDE = TRI_SIDE * 4;
/** 大三角形1個を分割してできる小三角形の数（原作 `_createTriangles16`）。 */
export const PANELS_PER_BIG_TRIANGLE = 16;

// ※ **15形状の中身（名前と構成）は [game/shapes.ts](../game/shapes.ts) が持つ**。
//   ここは「形が決まれば計算できる値」だけを扱い、パネル数は引数で受け取る
//   （形状テーブルを2箇所に置くと必ず食い違うため）。

// ----- 難易度＝爆弾率（原作 `BombPercentType` / `BOMB_PERCENT_*`） -----
export enum BombLevel {
  Easy = 0,
  Normal = 1,
  Hard = 2,
}

/** 爆弾率。爆弾数 = floor(パネル数 × 率)。 */
export const BOMB_PERCENT: readonly number[] = [0.1, 0.2, 0.3];

/** 難易度の数（3）。**15形状 × 3難易度 = 45ステージ**。 */
export const BOMB_LEVEL_COUNT = BOMB_PERCENT.length;

/** 爆弾数（原作 `setupBombs` と同じ切り捨て）。 */
export function bombCount(panelCount: number, level: BombLevel): number {
  return Math.floor(panelCount * BOMB_PERCENT[level]);
}

// ----- 隣接の重み（このゲームの核心） -----
// 三角形の隣接は6個で、**辺で接する3個が +2 / 頂点で接する3個が +1**。最大 3×2+3×1 = 9。
export const WEIGHT_SIDE = 2;
export const WEIGHT_APEX = 1;
/** パネルに出る数字の最大値（9）。 */
export const MAX_NUMBER = 3 * WEIGHT_SIDE + 3 * WEIGHT_APEX;

// ----- タイマー（原作 `MAX_TIMER_LIMIT`） -----
//
// ⚠️ **これは制限時間ではなく表示の上限**（2026-08-02 に原作を読み直して判明）。
// `MAX_TIMER_LIMIT` が使われているのは `GameTimerLayer:_drawGameTimer` の1箇所だけで、
// 15分を超えると `--:--` と出すためのもの。**時間切れのゲームオーバーは原作に無い**
// （タイマーは止まらず加算され続け、クリア判定にも関与しない）。
// スコアが所要時間で減っていく設計なので、遅ければ点が付かないだけで筋は通っている。
export const TIMER_DISPLAY_CAP_MIN = 15;
export const TIMER_DISPLAY_CAP_MS = TIMER_DISPLAY_CAP_MIN * 60 * 1000;

// ----- アンロック -----
/** 最初から開いている形状の数（原作 `CNT_INITIALOPEN_SHAPE`）。 */
export const INITIAL_UNLOCKED_SHAPES = 3;

/**
 * 🔴 **解放の条件は「通算★」**（2026-08-06・ユーザー決定）。★が **6個貯まるごとに1形状**開く。
 *
 * ## なぜ★なのか
 *
 * それまでは独自の「アンロックポイント」だったが、**画面のどこにも出ていない値**を
 * 条件にしていたので、プレイヤーには何をすれば開くのか分からなかった。
 * ★は形状選択画面のカードにも合計にも出ている＝**見えている値がそのまま条件**になる。
 *
 * ## 数の根拠（変えるときはここを再計算すること）
 *
 * - 1形状 = 3難易度 × 最大★3 ＝ **最大 9★** ／ 全体の上限は 45枠 × 3 ＝ **135★**
 * - 12形状ぶんを開くので全解放に **6 × 12 = 72★**（上限135★の約53%）
 * - ★は速さの評価（★3=基準時間内 / ★2=1.5倍 / ★1=2倍 / 超過は★0）なので、
 *   **クリアの回数ではなく上達が条件**になる
 *
 * ⚠️ **同時解放が起きない条件 ＝ この値が3以上であること**。1ゲームで増える★は
 * 高々3（1スロットの自己ベスト更新ぶん）なので、しきい値の間隔が3以上なら
 * 1回の結果で境界を2つ跨げない。**3未満に下げるときはこの性質が消える**ことに注意。
 *
 * ⚠️ 「詰む」の意味が旧ポイント制と違う。★は**自己ベストなので解き直せば増える**＝
 * 進めなくなっても行き止まりではなく**腕前のゲート**。旧制度は「クリア済み＝
 * それ以上稼げない」で本当の行き止まりだったので、そこだけは楽になっている。
 *
 * ※ 6 の想定（シミュレーション）: 全難易度を★2平均で回れば全解放（36プレイ）。
 *   Easy+Normal を★3で埋めても全解放。Easy のみ★3だと 5形状で要上達。
 *   8 にすると★2平均でも 9形状で止まる。
 */
export const UNLOCK_STARS_PER_SHAPE = 6;

/**
 * 形状 `shapeIndex`（0 起点）の解放に必要な通算★。
 * 初期解放ぶんは 0（＝常に開いている）。4番目が 6★、5番目が 12★…と等間隔。
 */
export function unlockThreshold(shapeIndex: number): number {
  if (shapeIndex < INITIAL_UNLOCKED_SHAPES) return 0;
  return UNLOCK_STARS_PER_SHAPE * (shapeIndex - INITIAL_UNLOCKED_SHAPES + 1);
}

// ----- スコアとメダル（★） -----
// ⚠️ **原作に無い新規設計**（Mac 版1.2 の1000点満点＋金銀銅メダルを復活させたもの）。
// **仮置きの値なので、遊んで調整する前提**（2026-08-01 決定）。式をここから散らさないこと。
export const SCORE_MAX = 1000;
/** 基準時間の「1パネルあたり秒数」。難易度別（Easy / Normal / Hard）。 */
export const SEC_PER_PANEL: readonly number[] = [1.0, 1.5, 2.0];
/** ★3 / ★2 / ★1 の上限倍率（基準時間の何倍まで）。超えると ★0（＝Pass）。 */
export const STAR_TIME_FACTOR: readonly number[] = [1.0, 1.5, 2.0];

/** 基準時間（ミリ秒）。 */
export function baseTimeMs(panelCount: number, level: BombLevel): number {
  return panelCount * SEC_PER_PANEL[level] * 1000;
}

/**
 * クリアスコア。**基準時間ちょうどで満点、倍かかると半分**（`1000 × T_base / T`）。
 * 速ければ速いほど良いが 1000 で頭打ち。0 未満にはならない。
 *
 * ⚠️ 原作 iPad 版にスコアは無く、これは **Mac 版1.2 の「1000点満点から所要時間で減点」を
 * 復活させた新規設計**（2026-08-01 決定）。**仮置きなので遊んで調整する**前提。
 */
export function computeScore(elapsedMs: number, base: number): number {
  const t = Math.max(1, elapsedMs); // 0 除算回避（0ms クリアは起こらないが式を安全に）
  return Math.min(SCORE_MAX, Math.round((SCORE_MAX * base) / t));
}

/**
 * 獲得スター（メダル）。**金=★3 / 銀=★2 / 銅=★1 / Pass=★0**。
 * 基準時間の 1.0 / 1.5 / 2.0 倍が境目（`STAR_TIME_FACTOR`）。
 * クリアしても基準の2倍を超えたら ★0 ＝ 記録は残るがランキングには効かない。
 */
export function computeStars(elapsedMs: number, base: number): number {
  for (let i = 0; i < STAR_TIME_FACTOR.length; i++) {
    if (elapsedMs <= base * STAR_TIME_FACTOR[i]) return 3 - i;
  }
  return 0;
}

// ----- タイトル表示 -----
// 原作は Info.plist の `CFBundleShortVersionString` / `NSHumanReadableCopyright` を
// `TitleLogoLayer` が読んで出していた。Web 版はここを唯一のソースとする
// （二角取りの `gameConfig.ts` と同じ扱い。バージョンは package.json と手動同期）。
export const APP_VERSION = '0.1.0';
export const COPYRIGHT_TEXT = 'COPYRIGHT(C) 2026 HONGSOFT.JP ALL RIGHT RESERVED.';
/** タイトルロゴの文字（原作 `TitleLogoLayer` の `CCLabelTTF`）。**画像ではない**。 */
export const TITLE_LOGO_TEXT = 'Triangle Sweeper';
/** ロゴのフェードイン秒数（原作 `CCActionFadeIn actionWithDuration:1.5f`）。 */
export const TITLE_FADE_MS = 1500;

// ----- ランキング -----
/**
 * `stage_key` ＝ `S{形状番号2桁}B{爆弾率}`（例 `S03B2` = Three Triangle × Hard）。
 * サーバーの `STAGE_KEY_RE = /^[A-Za-z0-9_-]{1,24}$/` を満たす。
 */
export function stageKey(shapeIndex: number, level: BombLevel): string {
  return `S${String(shapeIndex + 1).padStart(2, '0')}B${level}`;
}

/**
 * 画面に出す**表示用**のステージ名（例 `S01B1`）。爆弾率を **1..3** で見せる
 * （2026-08-05・ユーザー指定。`B0` は「無い」と読めてしまうため）。
 *
 * 🔴 **`stageKey()` とは1つずれる。保存側に使わないこと。**
 * D1 の `scores.stage_key` に入るのは `stageKey()` のほうで、そちらは
 * **2026-08-02 の公開時から `B0..B2`** で記録されている。ここを保存に使うと
 * 既存の記録が全部別のステージ扱いになって迷子になる（後から気づけない壊れ方）。
 * 形状の並び順を変えられないのと同じ理由＝**公開済みの記録の互換性**。
 */
export function stageLabel(shapeIndex: number, level: BombLevel): string {
  return `S${String(shapeIndex + 1).padStart(2, '0')}B${level + 1}`;
}
